import { fmt2 } from "@/lib/api";
import { fl } from "@/lib/finI18n";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ROMAN = ["I", "II", "III", "IV"];
const ROW_CLS = { section: "bg-secondary/60 font-semibold text-xs uppercase tracking-wide", total: "font-semibold border-t-2 border-primary/30", grand: "font-bold bg-primary/5 border-t-2 border-primary" };

function Pick({ value, onChange, opts, testid, w = "w-32" }) {
  return (
    <Select value={String(value)} onValueChange={onChange}>
      <SelectTrigger className={`${w} h-9`} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function LkTable({ rows, cols, fmt, testid }) {
  return (
    <div className="overflow-x-auto">
      <Table data-testid={testid}>
        <TableHeader><TableRow>{cols.map(([k, l], i) => <TableHead key={k} className={i ? "text-right whitespace-nowrap" : ""}>{l}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key} className={ROW_CLS[r.lvl] || ""} data-testid={`${testid}-row-${r.key}`}>
              <TableCell className={r.lvl === "line" ? "pl-6 text-sm" : "text-sm"}>{r.label}</TableCell>
              {cols.slice(1).map(([k]) => (
                <TableCell key={k} className="num text-right whitespace-nowrap text-sm" data-testid={`${testid}-${r.key}-${k}`}>{r.lvl === "section" ? "" : fmt(r[k])}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function LKPMView({ report, lang, ptype, pno, year, onType, onNo, onYear }) {
  const L = report?.labels || {};
  const now = new Date().getFullYear();
  const nos = (ptype === "s" ? [1, 2] : [1, 2, 3, 4]).map((x) => [x, `${fl(ptype === "s" ? "lkSemester" : "lkQuarter", lang)} ${ROMAN[x - 1]}`]);
  return (
    <Card className="p-5 space-y-5" data-testid="lkpm-view">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><label className="text-xs text-muted-foreground">{fl("lkType", lang)}</label>
          <Pick value={ptype} onChange={onType} opts={[["q", fl("lkQuarter", lang)], ["s", fl("lkSemester", lang)]]} testid="lkpm-type" /></div>
        <div className="space-y-1"><label className="text-xs text-muted-foreground">{fl("lkNo", lang)}</label>
          <Pick value={pno} onChange={(v) => onNo(Number(v))} opts={nos} testid="lkpm-no" w="w-40" /></div>
        <div className="space-y-1"><label className="text-xs text-muted-foreground">{fl("lkYear", lang)}</label>
          <Pick value={year} onChange={(v) => onYear(Number(v))} opts={Array.from({ length: 11 }, (_, i) => [now - i, now - i])} testid="lkpm-year" w="w-28" /></div>
        {report && (
          <div className="ml-auto text-right">
            <p className="font-head font-semibold" data-testid="lkpm-period-label">{report.period.label}</p>
            <p className="text-[11px] text-muted-foreground">{report.period.date_from} – {report.period.date_to}</p>
          </div>
        )}
      </div>
      {!report && <p className="py-10 text-center text-sm text-muted-foreground" data-testid="lkpm-loading">{fl("loading", lang)}</p>}
      {report && <LkBody report={report} L={L} lang={lang} />}
    </Card>
  );
}

function LkBody({ report, L, lang }) {
  return (
    <>
      <p className="text-[11px] text-muted-foreground">{fl("lkHint", lang)}</p>
      <section className="space-y-2">
        <h3 className="font-head font-semibold text-sm">{L.inv_title}</h3>
        <LkTable rows={report.investment} cols={[["label", L.comp], ["prev", L.prev], ["add", L.add], ["total", L.total]]} fmt={(v) => fmt2(v || 0)} testid="lkpm-inv" />
      </section>
      <section className="space-y-2">
        <h3 className="font-head font-semibold text-sm">{L.wf_title}</h3>
        <LkTable rows={report.workforce} cols={[["label", L.cat], ["before", L.before], ["add", L.wadd], ["less", L.less], ["total", L.wtotal]]} fmt={(v) => `${v ?? 0}`} testid="lkpm-wf" />
      </section>
      <ul className="text-[11px] text-muted-foreground list-disc pl-4 space-y-0.5" data-testid="lkpm-notes">{report.notes.map((x) => <li key={x}>{x}</li>)}</ul>
    </>
  );
}
